import { createSelector } from 'reselect';

import Platform from '../../../utils/Platform';
import makeSelectMain, { getIsSso } from '../../Main/selectors';
import { getProperBiometricLabel } from './helpers';
import { initialState } from './reducer';

/**
 * Direct selector to the Security state domain
 */
const selectSecurityDomain = (state) => state.security || initialState;

/**
 * Other specific selectors
 */

/**
 * @description Selects security settings to display on desktop (assuming security section is
 * rendered at all; selectShouldRenderIfNonSsoOrhasNativeSso should be used to
 * conditionally render the entire security section.)
 */
const selectDesktopSecurityProps = () => createSelector(
  makeSelectSecurity('isBiometricLoginEnabled'),
  makeSelectSecurity('isPinEnabled'),
  makeSelectMain('isSslAuth'),
  getIsSso(),
  (
    isBiometricLoginEnabled,
    isPinEnabled,
    isSslAuth,
    isSso,
  ) => ({
    heading: 'Security',
    rowsData: [
      ...((isSslAuth || isSso) ? [] : [{
        buttonProps: {
          disabled: false,
          label: 'edit password',
          route: '/settings/password',
        },
        details: '',
        label: 'Password',
        loading: false,
      }]),
      {
        buttonProps: {
          disabled: true,
          label: 'update biometric id in app',
        },
        details: isBiometricLoginEnabled ? 'On' : 'Off',
        label: 'Biometric ID (Update in App)',
        loading: false,
      },
      {
        buttonProps: {
          disabled: true,
          label: 'update pin in app',
        },
        details: isPinEnabled ? 'On' : 'Off',
        label: 'Pin (Update in App)',
        loading: false,
      },
    ],
    // eslint-disable-next-line max-len
    text: 'Keep your account secure by updating your password regularly. Biometric ID and Pin login are available in the app and can be enabled in the app settings.',
  }),
);

/**
 * @description Selects a type of error from the primary the error object
 */
const selectSecurityError = (prop) => createSelector(
  makeSelectSecurity('errors'),
  (errorState) => errorState[prop],
);

/**
 * @description Selects a specific error from the bannerErrors object
 */
const selectSecurityBannerError = (prop) => createSelector(
  selectSecurityError('bannerError'),
  (errorState) => errorState[prop],
);

/**
 * @description Selects string to display in "Sign In Method" row button
 */
const selectSignInMethodButtonDetails = () => createSelector(
  makeSelectSecurity('isBiometricLoginEnabled'),
  makeSelectSecurity('isPinEnabled'),
  selectPrettySupportedBiometricAuthenticationTypes(),
  (isBiometricLoginEnabled, isPinEnabled, prettySupportedBiometricAuthenticationTypes) => {
    const arr = [];
    if (isPinEnabled) {
      arr.push('Pin');
    }
    if (isBiometricLoginEnabled) {
      arr.push(getProperBiometricLabel(Platform.OS, prettySupportedBiometricAuthenticationTypes));
    }
    return arr.length ? `${arr.join(', ')} Enabled` : '';
  },
);

const selectInputFocusState = (field) => createSelector(
  makeSelectSecurity('focus'),
  (focus) => focus[field],
);

/**
 * @description Converts numeric representations of supported biometric authentication types to
 * string versions
 * @returns {() => {
 *  face: boolean,
 *  fingerprint: boolean,
 *  iris: boolean,
 * }}
 */
const selectPrettySupportedBiometricAuthenticationTypes = () => createSelector(
  makeSelectSecurity('supportedBiometricAuthenticationTypes'),
  (numericSupportedBiometricAuthenticationTypes) => {
    const initialAccumulator = {
      face: false,
      fingerprint: false,
      iris: false,
    };
    return numericSupportedBiometricAuthenticationTypes.reduce((acc, numValue) => {
      if (numValue === 1) return { ...acc, fingerprint: true };
      if (numValue === 2) return { ...acc, face: true };
      // Only other possible value should be '3'
      return { ...acc, iris: true };
    }, initialAccumulator);
  },
);

const selectSecurityScreenReaderNotifications = () => createSelector(
  selectSecurityError('bannerError'),
  ({ pinDrawer, signInMethods }) => {
    const nonFormErrors = [pinDrawer, signInMethods];
    return nonFormErrors.filter((error) => !!error);
  },
);

const selectSignInMethodsNotifications = () => createSelector(
  selectSecurityBannerError('signInMethods'),
  (error) => error ? [error] : [],
);

const selectPinDrawerNotifications = () => createSelector(
  selectSecurityBannerError('pinDrawer'),
  (error) => error ? [error] : [],
);

/**
 * Default selector used by Security
 */
const makeSelectSecurity = (prop) => createSelector(
  selectSecurityDomain,
  (state) => state[prop],
);

export default makeSelectSecurity;
export {
  selectDesktopSecurityProps,
  selectInputFocusState,
  selectPinDrawerNotifications,
  selectPrettySupportedBiometricAuthenticationTypes,
  selectSecurityBannerError,
  selectSecurityError,
  selectSecurityScreenReaderNotifications,
  selectSignInMethodButtonDetails,
  selectSignInMethodsNotifications,
};
